package com.evoapps.evoshift.domain.timeline

import com.evoapps.evoshift.domain.market.payroll.PayrollFact
import com.evoapps.evoshift.domain.model.CheckStatus
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.withoutStoredEmployerCost
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.StaffChecks
import com.evoapps.evoshift.domain.model.StaffRecord
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/*
 * SH-212: a person's timeline at one workplace: what applies to them from which day (and until
 * when), on one time axis the owner and financial managers edit.
 *
 * The timeline owns no data of its own except free notes. Every other entry is a view of a field
 * that already exists, and an edit is written back there (docs/employee-timeline.md):
 * - hire and end of cooperation: the engagement's startDate / endDate / isActive
 *   (users/{uid}.engagements.{wpId});
 * - contract (type, declared hours), pay (raises, FTE) and payroll facts (student status, the
 *   tax reliefs): the dated histories of the pay terms (users/{uid}/sensitive/{wpId}: rateHistory
 *   SH-57, profileHistory SH-149, contractHistory SH-212), whose current fields stay those valid
 *   today ([FinancialEngagement.normalizedOn]);
 * - OHS training and medical exam: the HR dates (users/{uid}/hr/{wpId}, SH-47), with the earlier
 *   ones in its history (SH-212);
 * - notes: users/{uid}/timeline/{wpId}, the only new data;
 * - SH-232: working without pay: the workplace's periods (settings.unpaidPeriods.{uid},
 *   UnpaidWork), and not counting in the labour cost and SPMH: the months of SH-206's setting
 *   (workplaces/{wpId}/labour_inclusion/{uid}, LabourInclusion) where the person does not count.
 */

/** The kinds of entries, one lane each (tax reliefs: one lane per relief). */
enum class TimelineKind {
    HIRE, END, CONTRACT, PAY, STUDENT, TAX_RELIEF,
    /** SH-232: working without pay (0,00 zł): from–to. */
    UNPAID,
    /** SH-232: not counted in the labour cost, labour % and SPMH: whole months, from–to. */
    NOT_COUNTED,
    OHS_TRAINING, MEDICAL_EXAM, NOTE;

    /**
     * Entries that change what the person's shifts cost (contract, pay, payroll facts, working
     * without pay) or what the labour figures hold (not counted): a change recalculates the open months.
     */
    val affectsCost: Boolean
        get() = this == CONTRACT || this == PAY || this == STUDENT || this == TAX_RELIEF || this == UNPAID || this == NOT_COUNTED

    /** Entries kept in the pay terms: only with pay terms at the workplace. */
    val needsPayTerms: Boolean get() = this == CONTRACT || this == PAY || this == STUDENT || this == TAX_RELIEF

    /** SH-232: periods that never overlap within their lane (statuses, reliefs, working without pay, not counted). */
    val isSpan: Boolean get() = this == STUDENT || this == TAX_RELIEF || this == UNPAID || this == NOT_COUNTED

    /** One entry follows the other: each lasts until the next one starts (no end of its own). */
    val isSequential: Boolean get() = this == CONTRACT || this == PAY

    /** Entries with an end of their own (from–to, "valid until"). */
    val hasEnd: Boolean get() = isSpan || this == OHS_TRAINING || this == MEDICAL_EXAM

    /** Single-day markers. */
    val isMarker: Boolean get() = this == HIRE || this == END || this == NOTE

    val staffCheck: StaffCheck?
        get() = when (this) {
            OHS_TRAINING -> StaffCheck.OHS_TRAINING
            MEDICAL_EXAM -> StaffCheck.MEDICAL_EXAM
            else -> null
        }

    companion object {
        fun of(check: StaffCheck): TimelineKind = when (check) {
            StaffCheck.OHS_TRAINING -> OHS_TRAINING
            StaffCheck.MEDICAL_EXAM -> MEDICAL_EXAM
        }
    }
}

/**
 * One entry. [from] null: "from the start" (pay terms or facts from before dates were kept) or
 * not known (a medical exam recorded with its validity only). [until]: the last day (inclusive)
 * of an entry with an end of its own ([TimelineKind.hasEnd]); a sequential entry's end follows
 * from the next one ([EmployeeTimeline.endOf]). The values depend on the [kind].
 */
data class TimelineEntry(
    val kind: TimelineKind,
    val from: LocalDate?,
    val until: LocalDate? = null,
    /** CONTRACT: a SystemContractType name (or an older free-text id) and the monthly hours of hourly pay. */
    val contractTypeId: String? = null,
    val declaredHours: Int? = null,
    /** PAY: the terms (hourly rate, or salary with its FTE). */
    val pay: CompensationModel? = null,
    /** TAX_RELIEF: which one ([com.evoapps.evoshift.domain.market.payroll.PayrollRules.taxReliefs]). */
    val fact: PayrollFact? = null,
    /** NOTE: its text and stable id. */
    val text: String? = null,
    val noteId: String? = null
) {
    /** Unique within a timeline (validation keeps sequential starts and spans of a lane apart). */
    val id: String
        get() = when (kind) {
            TimelineKind.HIRE, TimelineKind.END -> kind.name
            TimelineKind.NOTE -> "NOTE:${noteId.orEmpty()}"
            TimelineKind.TAX_RELIEF -> "TAX_RELIEF:${fact?.name}:${from ?: ""}"
            TimelineKind.OHS_TRAINING, TimelineKind.MEDICAL_EXAM -> "${kind.name}:${from ?: ""}:${until ?: ""}"
            else -> "${kind.name}:${from ?: ""}"
        }

    /** The lane it is drawn in: the kind, and for tax reliefs which one. */
    val lane: TimelineLane get() = TimelineLane(kind, fact.takeIf { kind == TimelineKind.TAX_RELIEF })

    /** Whether [day] is within this entry's own dates (null ends are open). */
    fun covers(day: LocalDate?, end: LocalDate? = until): Boolean {
        if (day == null) return from == null
        return (from == null || day >= from) && (end == null || day <= end)
    }

    /** The same entry [days] later (dragging: start and end move together). */
    fun movedBy(days: Int): TimelineEntry = copy(
        from = from?.plus(DatePeriod(days = days)),
        until = until?.plus(DatePeriod(days = days))
    )

    /** What it says, without its dates. */
    fun sameValuesAs(other: TimelineEntry): Boolean =
        copy(from = null, until = null) == other.copy(from = null, until = null)
}

/**
 * SH-250: redundant entries are one. Within a lane, an entry that says the same as the one before
 * it in every term ([TimelineEntry.sameValuesAs]: for pay the model, rate or salary, FTE and
 * currency, not the unused stored employer cost; for a contract its type and hours; a relief the
 * same relief) and follows it without a gap is folded into it:
 * - contract and pay (sequential): one lasts until the next starts, so they always follow on;
 * - spans (student, reliefs, without pay, not counted): only when the next one starts the day
 *   after the last one ends (or within it); an open-ended span takes in everything after it.
 * Markers, notes and OHS / medical records (each one a separate fact) never merge. What applies on
 * any day stays the same, so nothing is repriced and past rosters keep their cost snapshots.
 */
object TimelineMerge {
    /** [entries] with the redundant ones folded in, the rest in their order. */
    fun merged(entries: List<TimelineEntry>): List<TimelineEntry> {
        // By position: the entry each kept one becomes, and the ones folded away (null).
        val result = arrayOfNulls<TimelineEntry>(entries.size)
        entries.indices.filter { entries[it].kind.isSequential || entries[it].kind.isSpan }
            .groupBy { entries[it].lane }.values.forEach { lane ->
                var kept: Int? = null
                lane.sortedWith(compareBy(nullsFirst()) { entries[it].from }).forEach { i ->
                    val entry = entries[i]
                    val last = kept?.let { result[it] }
                    if (last != null && sameTerms(last, entry) && follows(last, entry)) {
                        if (!entry.kind.isSequential) {
                            result[kept!!] = last.copy(until = if (last.until == null || entry.until == null) null else maxOf(last.until, entry.until))
                        }
                    } else {
                        result[i] = entry
                        kept = i
                    }
                }
            }
        return entries.mapIndexedNotNull { i, entry ->
            if (entry.kind.isSequential || entry.kind.isSpan) result[i] else entry
        }
    }

    /** The same values, the stored employer cost of pay left out (RatePeriod.sameTermsAs). */
    private fun sameTerms(a: TimelineEntry, b: TimelineEntry): Boolean =
        a.copy(pay = a.pay?.withoutStoredEmployerCost()).sameValuesAs(b.copy(pay = b.pay?.withoutStoredEmployerCost()))

    /** Whether [next] (starting no earlier) follows [last] without a day between them. */
    private fun follows(last: TimelineEntry, next: TimelineEntry): Boolean {
        if (last.kind.isSequential) return true
        val end = last.until ?: return true
        val start = next.from ?: return true
        return start <= end.plus(DatePeriod(days = 1))
    }
}

/** A lane of the wide timeline (and a section of the phone list). */
data class TimelineLane(val kind: TimelineKind, val fact: PayrollFact? = null)

/** A free note on a day (users/{uid}/timeline/{wpId}). */
data class TimelineNote(val id: String, val on: LocalDate, val text: String)

/**
 * Where the person's shifts at the workplace lie, for the checks of the hire and the end. SH-237:
 * the shifts they work (Shift.workerId): covered ones count for who covered them, a no-show for nobody.
 */
data class ShiftDays(val first: LocalDate?, val last: LocalDate?) {
    val any: Boolean get() = first != null

    companion object {
        val NONE = ShiftDays(null, null)
    }
}

/** The data the timeline is a view of (see the file comment for where each part lives). */
data class TimelineSources(
    val engagement: Engagement?,
    /** Null: no pay terms at the workplace (or not readable): the cost lanes are not offered. */
    val financial: FinancialEngagement?,
    val staff: StaffRecord?,
    val notes: List<TimelineNote> = emptyList(),
    /** SH-232: the person's periods without pay; null: the lane is not shown (the workplace's owner, a viewer who may not see pay). */
    val unpaid: List<com.evoapps.evoshift.domain.budget.UnpaidPeriod>? = null,
    /** SH-232: whether they count in the labour figures; null: not shown (a viewer without VIEW_FINANCES, or not readable). */
    val labour: LabourSource? = null,
    /** The day after the end of cooperation the timeline planned (users/{uid}/timeline/{wpId}.plannedEndOn), when one. */
    val plannedEndOn: LocalDate? = null
) {
    /**
     * SH-234: an end of cooperation that no longer applies: the engagement is active although its
     * end date has passed, because it was re-activated (the employee form's re-hire sets isActive
     * back and keeps the old endDate). Not one the timeline planned that the morning job has yet
     * to apply (endDate + 1 = [plannedEndOn]).
     */
    fun endIsStale(today: LocalDate): Boolean {
        val e = engagement ?: return false
        val end = day(e.endDate) ?: return false
        return e.isActive && end < today && plannedEndOn != end.plus(DatePeriod(days = 1))
    }
}

/**
 * SH-232: what decides in which months a person counts in the labour cost, labour % and SPMH
 * (LabourInclusion.counts): their setting's [periods] and the facts of the default.
 */
data class LabourSource(
    val periods: List<com.evoapps.evoshift.domain.budget.LabourInclusionPeriod>,
    val hasPayTerms: Boolean,
    val isOwnerLevel: Boolean,
    /** The workplace's owner whose shifts are unpaid (WorkplacePayrollSettings.ownerShiftsUnpaid). */
    val ownerShiftsUnpaid: Boolean = false,
    /** Their periods without pay (a month with no paid day is not paid work). */
    val unpaid: List<com.evoapps.evoshift.domain.budget.UnpaidPeriod> = emptyList()
) {
    private fun facts(month: String) = com.evoapps.evoshift.domain.budget.LabourInclusionFacts(
        hasPayTerms = hasPayTerms,
        isOwnerLevel = isOwnerLevel,
        isPaidWork = !ownerShiftsUnpaid && com.evoapps.evoshift.domain.budget.UnpaidWork.paidIn(unpaid, month)
    )

    /** Whether they count in [month] ("YYYY-MM") without a setting. */
    fun defaultIn(month: String): Boolean = com.evoapps.evoshift.domain.budget.LabourInclusion.byDefault(facts(month))

    /** Whether they count in [month] with [periods] as the setting. */
    fun countsIn(month: String, periods: List<com.evoapps.evoshift.domain.budget.LabourInclusionPeriod> = this.periods): Boolean =
        com.evoapps.evoshift.domain.budget.LabourInclusion.settingIn(periods, month) ?: defaultIn(month)
}

/** A person's timeline at one workplace on [today]. */
data class EmployeeTimeline(
    val userId: String,
    val workplaceId: String,
    val entries: List<TimelineEntry>,
    val today: LocalDate,
    val hasPayTerms: Boolean,
    /** The tax reliefs of the workplace's market, one lane each. */
    val reliefs: List<PayrollFact>,
    /** The current OHS training and medical exam (whose expiry is warned about), when recorded. */
    val currentChecks: Map<StaffCheck, String> = emptyMap(),
    /** SH-232: the "Works without pay" lane is shown (the viewer may see pay, not the workplace's owner). */
    val showsUnpaid: Boolean = false,
    /** SH-232: the "Labour cost and SPMH" lane is shown (owners and financial managers). */
    val showsLabour: Boolean = false
) {
    val hire: TimelineEntry? get() = entries.firstOrNull { it.kind == TimelineKind.HIRE }
    val end: TimelineEntry? get() = entries.firstOrNull { it.kind == TimelineKind.END }

    fun entry(id: String): TimelineEntry? = entries.firstOrNull { it.id == id }

    /** The lanes to show, in order: the cost lanes only with pay terms. */
    val lanes: List<TimelineLane>
        get() = buildList {
            add(TimelineLane(TimelineKind.HIRE))
            if (hasPayTerms) {
                add(TimelineLane(TimelineKind.CONTRACT))
                add(TimelineLane(TimelineKind.PAY))
                add(TimelineLane(TimelineKind.STUDENT))
                reliefs.forEach { add(TimelineLane(TimelineKind.TAX_RELIEF, it)) }
            }
            if (showsUnpaid) add(TimelineLane(TimelineKind.UNPAID))
            if (showsLabour) add(TimelineLane(TimelineKind.NOT_COUNTED))
            add(TimelineLane(TimelineKind.OHS_TRAINING))
            add(TimelineLane(TimelineKind.MEDICAL_EXAM))
            add(TimelineLane(TimelineKind.NOTE))
        }

    /** The entries of [lane], by start ("from the start" first). Hire and end share the first lane. */
    fun entriesOf(lane: TimelineLane): List<TimelineEntry> = entries.filter {
        if (lane.kind == TimelineKind.HIRE) it.kind == TimelineKind.HIRE || it.kind == TimelineKind.END else it.lane == lane
    }.sortedWith(compareBy(nullsFirst()) { it.from })

    /**
     * The first day [entry] applies: its own, or for "from the start" the hire (when known).
     * Null: from before anything the timeline knows.
     */
    fun startOf(entry: TimelineEntry): LocalDate? = entry.from ?: if (entry.kind.isSequential || entry.kind.hasEnd) hire?.from else null

    /**
     * The last day [entry] applies (inclusive); null: open-ended. A sequential entry lasts until
     * the day before the next one of its lane starts, and at most until the end of cooperation.
     */
    fun endOf(entry: TimelineEntry): LocalDate? {
        if (entry.kind.isMarker) return entry.from
        if (entry.kind.hasEnd) return entry.until
        val next = entriesOf(entry.lane).mapNotNull { it.from }.filter { entry.from == null || it > entry.from }.minOrNull()
        val lastBeforeNext = next?.minus(DatePeriod(days = 1))
        val endOfCooperation = end?.from
        return listOfNotNull(lastBeforeNext, endOfCooperation).minOrNull()
    }

    /** The entry of [lane] in force today (sequential lanes), the one the current fields hold. */
    fun currentOf(lane: TimelineLane): TimelineEntry? {
        val list = entriesOf(lane)
        return list.lastOrNull { it.from == null || it.from <= today } ?: list.firstOrNull()
    }

    /**
     * The expiry marker of an OHS training or medical exam: EXPIRING or EXPIRED for the current
     * one only (a training replaced by a newer one has nothing left to warn about).
     */
    fun warningOf(entry: TimelineEntry): CheckStatus? {
        val check = entry.kind.staffCheck ?: return null
        if (currentChecks[check] != entry.id) return null
        return StaffChecks.status(entry.until, today).takeIf { it == CheckStatus.EXPIRING || it == CheckStatus.EXPIRED }
    }

    /** The days the wide timeline spans: from the earliest date to the latest, today included. */
    val range: ClosedRange<LocalDate>
        get() {
            val days = entries.flatMap { listOfNotNull(it.from, it.until) } + today
            return days.min()..days.max()
        }

    /**
     * SH-224: the days the wide timeline's axis spans (and the requests are read for): from a
     * fortnight before the earliest date to a fortnight after six months from today (or the
     * latest date).
     */
    val axis: ClosedRange<LocalDate>
        get() {
            val first = range.start.minus(DatePeriod(days = 14))
            val last = maxOf(range.endInclusive, today.plus(DatePeriod(months = 6))).plus(DatePeriod(days = 14))
            return first..last
        }

    /** What applies on [day] (null: before anything known) for the cost lanes: compared to find what a change reprices. */
    internal fun costStateOn(day: LocalDate?): List<Any?> = buildList {
        fun sequential(kind: TimelineKind): TimelineEntry? {
            val list = entries.filter { it.kind == kind }.sortedWith(compareBy(nullsFirst()) { it.from })
            val inForce = if (day == null) list.firstOrNull { it.from == null } else list.lastOrNull { it.from == null || it.from <= day }
            return (inForce ?: list.firstOrNull())?.copy(from = null, until = null)
        }
        add(sequential(TimelineKind.CONTRACT))
        add(sequential(TimelineKind.PAY))
        add(entries.any { it.kind == TimelineKind.STUDENT && it.covers(day) })
        (reliefs + entries.mapNotNull { it.fact }).distinct().sortedBy { it.ordinal }.forEach { fact ->
            add(fact to entries.any { it.kind == TimelineKind.TAX_RELIEF && it.fact == fact && it.covers(day) })
        }
        // SH-232: working without pay (the shifts' cost) and not counting (the labour figures).
        add(TimelineKind.UNPAID to entries.any { it.kind == TimelineKind.UNPAID && it.covers(day) })
        add(TimelineKind.NOT_COUNTED to entries.any { it.kind == TimelineKind.NOT_COUNTED && it.covers(day) })
    }
}
